import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridColDef,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import useAuth from 'auth/UseAuth';
import { ReactNode, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  GetSampleTrackingCompletedSamples,
  GetSampleTrackingTransitionSamples,
  SampleTrackingTableData,
  SelectedSampleInfo,
  TransitionSample,
  TransitionType,
} from '../../data/SampleTrackingData';
import { CreateSampleTrackingTransitionModal } from './CreateSampleTrackingTransitionModal';
import { AuthorizedSection } from '../../auth/AuthorizedSection';
import {
  dateGetter,
  generateWidthFromProperty,
  sampleTrackingGridDefaultColumnOverrides,
} from '../../util/grid/TableUtils';
import {
  checkByFailedReason,
  compactGridHeaderClassName,
  copyBtn,
  createdAt,
  enteredAt,
  exitedAt,
  foreignHash,
  informaticsRunStatus,
  inputForeignHash,
  inputId,
  inputLabAssignedId,
  inputSampleBbid,
  inputSampleId,
  labAssignedSampleId,
  multiQc,
  newForeignHash,
  newId,
  newLabAssignedId,
  newSampleBbid,
  newSampleId,
  checkByStatus,
  r1FastQLocation,
  r2FastQLocation,
  sampleBbid,
  sampleId,
  sequenceRunId,
  sequenceType,
  stGridFulfillmentPlanSampleSelectionStatus,
  stGridSampleSampleTypeId,
  transitionId,
  wallClock,
  sampleTag,
  qcStatus,
} from '../../util/Constants';
import { getTransitionSampleIdentifier, Sample, truncateNoDashGuid } from '../../data/SampleData';
import { CustomizableSwitch } from '../../components/CustomizableSwitch';
import { SampleTrackingRowSelectionModal } from './SampleTrackingRowSelectionModal';
import { filter, find, flatMap, keyBy, map, unionBy, uniq } from 'lodash';
import { renderSampleHierarchyGridCell } from '../../components/grid/cell/SampleHierarchyGridCell';
import { GetOrderScopedSampleTrackingTableData } from '../../data/SampleTrackingTransitionData';
import { grey } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import { LoadingState, LoadState } from '../../components/LoadingStateUtil';
import { InformaticsCheckByModal } from './InformaticsCheckByModal';
import { renderCellMultiQcDownloadButton } from 'components/grid/GridCellMultiQcDownloadButton';
import { FlexTableBox } from 'components/FlexTableBox';
import { GroupByAttribute, SampleTrackingGroupByAttributes } from '../../components/SampleTrackingGroupByAttributes';
import useMemoTranslation from 'hooks/UseMemoTranslation';
import { CheckByFailedReason } from 'data/ReferenceData';
import { GetSampleSequencingDetails, SampleSequencingDetails, SequencingFastQ } from 'data/SampleHierarchyData';
import { SampleTrackingOrderGridDetailsModal } from './orders/SampleTrackingOrderDetailsModal';
import { SampleTrackingOrderRow } from './orders/SampleTrackingOrderGrid';
import { SampleHierarchyDialog } from '../sampleHierarchy/SampleHierarchyDialog';
import { FlexBox } from '../../components/FlexBox';
import { renderGuidCellTooltip } from '../../components/grid/cell/GuidGridCellTooltip';
import { CheckByModal } from './CheckByModal';
import { SampleTrackingCountsKpi } from '../../components/SampleTrackingCountsKpi';
import useCheckByFailedReasons from '../../components/hooks/UseCheckByFailedReasons';
import { useTransitionEnum } from '../../components/hooks/UseTransitionEnums';
import { renderGridCellInformaticsRunningIndicator } from '../../components/grid/GridCellInformaticsRunningIndicator';
import { DecryptForeignHashesSwitch } from '../../components/DecryptForeignHashesSwitch';
import { SampleTrackingGridWrapper } from '../../components/grid/SampleTrackingGridWrapper';
import { GetCheckByGroupDetails } from '../../data/SampleTrackingCheckByGroupData';
import { renderCopyTextGridCell } from '../../components/grid/cell/CopyTextGridCell';
import { DialogOpenButton } from '../../components/DialogOpenButton';
import { WarningIndicator } from '../../components/WarningIndicator';
import HistoryIcon from '@mui/icons-material/History';
import { sampleTagGridColumnDef, SampleTrackingTagSamplesModal } from './SampleTrackingTagSamplesModal';
import { SampleTrackingDuplicateSamplesModal } from './SampleTrackingDuplicateSamplesModal';
import { SampleTrackingConfigProvider } from '../../contexts/SampleTrackingConfigContext';

interface BaseGridProps {
  researchProjectId: string;
  transitionType: TransitionType;
}

export interface TransitionRecordGridProps extends BaseGridProps {
  sampleGroup?: string;
  configuredTransitionId: string;
  setRefreshKpiTrigger: (refreshKpiTrigger: boolean) => void;
}

export interface TransitionSampleOrderScopedGridProps extends BaseGridProps {
  transitionIds: ReadonlyArray<string>;
  orderDetails: SampleTrackingOrderRow;
}

export interface TransitionSampleCheckByGroupScopedGridProps extends BaseGridProps {
  checkByGroupId: string;
}

interface GridProps {
  researchProjectId: string;
  transitionType: TransitionType;
  configuredTransitionId?: string;
  getData: (
    accessToken: string,
    useDecryptedHashes: boolean,
    loadCompletedSamples?: boolean
  ) => Promise<ReadonlyArray<SampleTrackingTableData>>;
  postDataFilter?: (rows: Row[]) => Row[];
  disableActions?: boolean;
  filterInactiveTransitionSamples?: boolean;
  columnsToHide?: ReadonlyArray<string>;
  customActions?: ReactNode;
  customKpis?: ReactNode;
  setRefreshKpiTrigger: (refreshKpiTrigger: boolean) => void;
}

type Row = Sample &
  TransitionSample &
  SampleSequencingDetails & {
    groupByPath?: string[] | undefined;
    id: number;
    newForeignHash?: string;
    newId?: string;
    newLabAssignedId?: string;
    newSampleBbid?: string;
    newSampleId?: string;
    inputForeignHash?: string;
    inputId: string;
    inputLabAssignedId?: string;
    inputSampleBbid?: string;
    inputSampleId?: string;
    filterId: string;
    selectionStatus?: string;
  };

type TransitionGroupByAttribute = GroupByAttribute<SampleTrackingTableData, Row>;

const useStyles = makeStyles({
  disabledRow: {
    backgroundColor: grey[200],
  },
  completedRow: {
    backgroundColor: '#e3f2fd',
    '&:hover': {
      backgroundColor: '#e3f2fd !important',
    },
  },
});

/** Unique identifier for this component. Used for view management persistence. Should not be changed once set. */
const COMPONENT_NAME = 'SampleTrackingTransitionSampleGrid';

export const TransitionSampleGrid = ({
  researchProjectId,
  transitionType,
  sampleGroup,
  configuredTransitionId,
  setRefreshKpiTrigger,
}: TransitionRecordGridProps) => {
  return (
    <>
      <Grid
        researchProjectId={researchProjectId}
        transitionType={transitionType}
        getData={async (accessToken: string, useDecryptedHashes: boolean, loadCompletedSamples: boolean = false) => {
          return await GetSampleTrackingTransitionSamples(
            researchProjectId,
            accessToken,
            configuredTransitionId,
            useDecryptedHashes,
            sampleGroup,
            loadCompletedSamples
          );
        }}
        configuredTransitionId={configuredTransitionId}
        columnsToHide={[exitedAt]}
        setRefreshKpiTrigger={setRefreshKpiTrigger}
        customKpis={
          <SampleTrackingCountsKpi
            researchProjectId={researchProjectId}
            configuredTransitionId={configuredTransitionId}
            sampleGroup={sampleGroup ?? 'all'}
          />
        }
      />
    </>
  );
};

export const TransitionSampleOrderScopedGrid = ({
  researchProjectId,
  transitionType,
  transitionIds,
  orderDetails,
}: TransitionSampleOrderScopedGridProps) => {
  return (
    <>
      <Grid
        researchProjectId={researchProjectId}
        transitionType={transitionType}
        getData={async (accessToken: string, useDecryptedHashes: boolean) => {
          const data = await Promise.all(
            transitionIds.map(
              async id =>
                await GetOrderScopedSampleTrackingTableData(id, researchProjectId, useDecryptedHashes, accessToken)
            )
          );

          const flattenedData = flatMap(data);

          const parentsWithDaughtersInTransition = new Set(
            flattenedData
              .filter(
                row =>
                  row.antecedentSample &&
                  row.antecedentTransitionSample?.transitionId === row.transitionSample?.transitionId
              )
              .map(row => row.antecedentSample!.sampleId)
          );

          // Filter out parent samples that have daughters in this transition to avoid dups
          return filter(flattenedData, row => !parentsWithDaughtersInTransition.has(row.sample.sampleId));
        }}
        disableActions={true}
        filterInactiveTransitionSamples={false}
        columnsToHide={[exitedAt, enteredAt, checkByStatus, checkByFailedReason, transitionId, newId, qcStatus]}
        customActions={<SampleTrackingOrderGridDetailsModal row={orderDetails} transitionType={transitionType} />}
        setRefreshKpiTrigger={() => {}}
      />
    </>
  );
};

export const TransitionSampleCheckByGroupScopedGrid = ({
  researchProjectId,
  transitionType,
  checkByGroupId,
}: TransitionSampleCheckByGroupScopedGridProps) => {
  return (
    <>
      <Grid
        researchProjectId={researchProjectId}
        transitionType={transitionType}
        getData={async (accessToken: string, useDecryptedHashes: boolean) => {
          const checkByScopedData = await GetCheckByGroupDetails(checkByGroupId, accessToken);

          const transitionIds = checkByScopedData.transitionIds;
          const transitionScopedData = await Promise.all(
            transitionIds.map(
              async id =>
                await GetOrderScopedSampleTrackingTableData(id, researchProjectId, useDecryptedHashes, accessToken)
            )
          );

          return flatMap(transitionScopedData);
        }}
        postDataFilter={(rows: Row[]): Row[] => {
          return rows.filter(row => row.transitionCheckByGroupId === checkByGroupId);
        }}
        disableActions={true}
        filterInactiveTransitionSamples={false}
        setRefreshKpiTrigger={() => {}}
      />
    </>
  );
};

export const Grid = ({
  researchProjectId,
  transitionType,
  getData,
  postDataFilter,
  disableActions = false,
  filterInactiveTransitionSamples: initialFilterInactiveTransitionSamples = true,
  columnsToHide,
  customActions,
  customKpis,
  configuredTransitionId,
  setRefreshKpiTrigger,
}: GridProps) => {
  const { accessToken } = useAuth();
  const classes = useStyles();
  const apiRef = useGridApiRef();
  const { t } = useMemoTranslation();

  const [loadingState, setLoadingState] = useState<LoadingState>({ status: 'NotStarted' });
  const [historyModalOpen, setHistoryModalOpen] = useState<boolean>(false);
  const [filterInactiveTransitionSamples, setFilterInactiveTransitionSamples] = useState<boolean>(
    initialFilterInactiveTransitionSamples
  );

  const [rawData, setRawData] = useState<ReadonlyArray<SampleTrackingTableData>>([]);
  const [rawDataCompletedSamples, setRawDataCompletedSamples] = useState<ReadonlyArray<SampleTrackingTableData>>([]);

  const [refreshTrigger, setRefreshTrigger] = useState<boolean>(false);

  const [completedSampleIds, setCompletedSampleIds] = useState<string[]>([]);
  const [selectedSampleIds, setSelectedSampleIds] = useState<string[]>([]);
  const [useDecryptedHashes, setUseDecryptedHashes] = useState<boolean>(false);
  const [useFilterOutOriginal, setUseFilterOutOriginal] = useState<boolean>(true);
  const [groupByEnabled, setGroupByEnabled] = useState<boolean>(transitionType === 'Subsetting');
  const [selectedGroupByAttributeId, setSelectedGroupByAttributeId] = useState<string>(
    transitionType === 'Subsetting' ? 'groupByParent' : ''
  );
  const [selectedGroupByAttribute, setSelectedGroupByAttribute] = useState<TransitionGroupByAttribute | undefined>();
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>();
  const [sampleSequencingDetails, setSampleSequencingDetails] = useState<ReadonlyArray<SampleSequencingDetails>>([]);
  const [sampleSequencingDetailsCompletedSamples, setSampleSequencingDetailsCompletedSamples] = useState<
    ReadonlyArray<SampleSequencingDetails>
  >([]);

  const transitionEnum = useTransitionEnum(transitionType);
  const checkByFailedReasons = useCheckByFailedReasons(transitionEnum?.transitionEnumId);

  const groupByAttributes = useMemo<TransitionGroupByAttribute[]>(
    () => [
      {
        id: 'groupByOrder',
        displayValue: t('groupByOrderId'),
        groupByPath: (resultRow: Row, ...rowData: SampleTrackingTableData[]) => {
          const row = rowData[0];
          resultRow.groupByPath = [row.transitionSample.transitionId, row.transitionSample.transitionSampleId];
          return resultRow;
        },
        groupingColumnDef: () => {
          return {
            headerName: t('byOrderId'),
            minWidth: 150,
            maxWidth: 300,
            //@ts-ignore  // allow implicit any
            valueFormatter: params => {
              const node = params.api.getRowNode(params.id!)!;
              if (node.type !== 'group') {
                return '';
              }

              return truncateNoDashGuid(node.groupingKey?.toString() ?? '');
            },
          };
        },
        show: true,
      },
      {
        id: 'groupByPatientDeidentity',
        displayValue: t('groupByPatientDeidentity'),
        groupByPath: (resultRow: Row, ...rowData: SampleTrackingTableData[]) => {
          const row = rowData[0];
          resultRow.groupByPath = [row.sample.patientDeidentity || '', row.transitionSample.transitionSampleId];
          return resultRow;
        },
        groupingColumnDef: () => {
          return {
            headerName: t('byPatientDeidentity'),
            minWidth: 300,
            maxWidth: 900,
            //@ts-ignore  // allow implicit any
            valueFormatter: params => {
              const node = params.api.getRowNode(params.id!)!;
              if (node.type !== 'group') {
                return '';
              }

              return node.groupingKey;
            },
          };
        },
        show: true,
      },
      {
        id: 'groupByParent',
        displayValue: t('groupByParent'),
        groupByPath: (resultRow: Row, ...rowData: SampleTrackingTableData[]) => {
          const row = rowData[0];
          const originalRow = rowData[1];

          if (!originalRow) {
            resultRow.groupByPath = [
              getTransitionSampleIdentifier(row.labAssignedSampleLabel, row.sample),
              row.transitionSample.transitionSampleId,
            ];
          } else {
            resultRow.groupByPath = [
              getTransitionSampleIdentifier(originalRow.labAssignedSampleLabel, originalRow.sample),
              getTransitionSampleIdentifier(row.labAssignedSampleLabel, row.sample),
            ];
          }

          return resultRow;
        },
        groupingColumnDef: () => {
          return {
            headerName: t('byParent'),
            minWidth: 300,
            maxWidth: 600,
            //@ts-ignore  // allow implicit any
            valueFormatter: params => {
              const node = params.api.getRowNode(params.id!)!;
              if (node.type !== 'group') {
                return '';
              }

              return node.groupingKey;
            },
          };
        },
        show: transitionType === 'Subsetting',
      },
    ],
    [transitionType, t]
  );

  useEffect(() => {
    if (groupByAttributes.length === 0) {
      return;
    }

    if (groupByEnabled) {
      setSelectedGroupByAttribute(groupByAttributes.find(attr => attr.id === selectedGroupByAttributeId));
    } else {
      setSelectedGroupByAttribute(undefined);
    }
  }, [groupByAttributes, groupByEnabled, selectedGroupByAttributeId]);

  const allData = useMemo(
    () => unionBy(rawData, rawDataCompletedSamples, item => item.sample.sampleId),
    [rawData, rawDataCompletedSamples]
  );
  const allSampleSequencingDetails = useMemo(
    () => unionBy(sampleSequencingDetails, sampleSequencingDetailsCompletedSamples, item => item.sampleId),
    [sampleSequencingDetails, sampleSequencingDetailsCompletedSamples]
  );
  const activeSampleIds = useMemo(() => {
    return uniq(rawData.map(i => i.sample.sampleId).filter(id => !completedSampleIds.includes(id)));
  }, [rawData, completedSampleIds]);

  const rows = useRows(
    allData,
    allSampleSequencingDetails,
    filterInactiveTransitionSamples,
    useFilterOutOriginal,
    postDataFilter,
    selectedGroupByAttribute
  );
  const columns = useColumns(transitionType, allData, checkByFailedReasons, columnsToHide);

  const isFetchingRef = useRef<boolean>(false);
  const isFetchingCompletedSamplesRef = useRef<boolean>(false);
  const completedSamplesLoaded = useRef<boolean>(false);

  useEffect(() => {
    if (
      !filterInactiveTransitionSamples &&
      completedSampleIds.length > 0 &&
      !completedSamplesLoaded.current &&
      !isFetchingCompletedSamplesRef.current
    ) {
      return LoadState(setLoadingState, async () => {
        if (!accessToken) {
          return;
        }

        try {
          isFetchingCompletedSamplesRef.current = true;

          const rows = await getData(accessToken, useDecryptedHashes, true);
          setRawDataCompletedSamples(rows);
          const sampleSequencingDetailsData = await GetSampleSequencingDetails(
            rows.map(i => i.sample.sampleId),
            accessToken
          );
          setSampleSequencingDetailsCompletedSamples(sampleSequencingDetailsData);
          completedSamplesLoaded.current = true;
        } finally {
          isFetchingCompletedSamplesRef.current = false;
        }
      });
    }
  }, [filterInactiveTransitionSamples, completedSampleIds, accessToken, useDecryptedHashes, getData]);

  useEffect(() => {
    if (isFetchingRef.current || !accessToken || researchProjectId === undefined) {
      return;
    }

    isFetchingRef.current = true;
    return LoadState(setLoadingState, async () => {
      try {
        const rows = await getData(accessToken, useDecryptedHashes);
        setRawData(rows);
        const sampleSequencingDetailsData = await GetSampleSequencingDetails(
          rows.map(i => i.sample.sampleId),
          accessToken
        );
        setSampleSequencingDetails(sampleSequencingDetailsData);

        if (configuredTransitionId) {
          const completedSampleIds = await GetSampleTrackingCompletedSamples(
            researchProjectId,
            configuredTransitionId,
            accessToken
          );
          setCompletedSampleIds(prev => {
            if (prev.length !== completedSampleIds.length) {
              completedSamplesLoaded.current = false;
            }

            return [...completedSampleIds];
          });
        }
      } finally {
        isFetchingRef.current = false;
      }
    });
  }, [accessToken, configuredTransitionId, researchProjectId, useDecryptedHashes, getData, refreshTrigger]);

  useEffect(() => {
    if (selectionModel) {
      const sampleIds: string[] = [];
      selectionModel.forEach(selected => {
        const id = apiRef.current?.getRow(selected)?.sampleId;
        if (id) {
          sampleIds.push(id);
        }
      });

      setSelectedSampleIds(uniq(sampleIds));
    }
  }, [selectionModel, apiRef]);

  const completedSamplesSelected = useMemo(() => {
    return selectedSampleIds.some(i => completedSampleIds.includes(i));
  }, [selectedSampleIds, completedSampleIds]);

  useEffect(() => {
    if (completedSamplesSelected) {
      setLoadingState({
        status: 'Warning',
        warningMessage: 'Actions are disabled for completed samples. Please deselect completed samples to continue.',
      });
    } else {
      setLoadingState({ status: 'NotStarted' });
    }
  }, [completedSamplesSelected]);

  function refreshData(hasChanges: boolean, selectionModel: GridRowSelectionModel = []) {
    if (!hasChanges) {
      return;
    }

    setRefreshKpiTrigger(true);
    setSelectionModel(selectionModel);
  }

  const getInformaticsCheckByTableData = useCallback(() => {
    return map(rows, i => {
      return {
        sampleId: i.sampleId ?? '',
        transitionSampleId: i.transitionSampleId ?? '',
        sampleIdentifier: i.inputId ?? '',
        selectionStatus: i.selectionStatus ?? '',
        checkByStatus: i.checkByStatus ?? '',
        sequenceType: i.sequenceType ?? '',
        sequenceRunId: i.sequenceRunId ?? '',
        informaticsPipelineRunId: i.informaticsPipelineRunId ?? '',
        qcStatus: i.qcStatus ?? '',
      };
    });
  }, [rows]);

  return (
    <FlexTableBox>
      <AuthorizedSection hasSampleTrackingViewAccess>
        <FlexBox sx={{ flexDirection: 'column' }}>{customKpis ?? <></>}</FlexBox>
      </AuthorizedSection>
      <SampleTrackingConfigProvider
        configuredTransitionId={configuredTransitionId}
        transitionEnumId={transitionEnum?.transitionEnumId}
        researchProjectId={researchProjectId}
      >
        <SampleTrackingGridWrapper
          apiRef={apiRef}
          leftActions={
            <FlexBox flexDirection='row' alignItems='center' flexWrap={'wrap'}>
              <DecryptForeignHashesSwitch decryptedHashesProps={[useDecryptedHashes, setUseDecryptedHashes]} />
              {!disableActions && (
                <>
                  <CustomizableSwitch
                    title={t('showExited')}
                    checked={!filterInactiveTransitionSamples}
                    onChange={() => setFilterInactiveTransitionSamples(!filterInactiveTransitionSamples)}
                  />
                  <CustomizableSwitch
                    title={t('filterOriginals')}
                    informationText={t('filterOutInputsWithAnOutputInfo')}
                    checked={useFilterOutOriginal}
                    onChange={() => setUseFilterOutOriginal(!useFilterOutOriginal)}
                  />
                  <SampleTrackingGroupByAttributes<SampleTrackingTableData, Row>
                    enabled={groupByEnabled}
                    setEnabled={setGroupByEnabled}
                    items={groupByAttributes}
                    selectedId={selectedGroupByAttributeId}
                    setSelectedId={setSelectedGroupByAttributeId}
                  />
                </>
              )}
            </FlexBox>
          }
          rightActions={
            <FlexBox flexDirection='row-reverse' alignItems='center' flexWrap={'wrap'}>
              {!disableActions && (
                <AuthorizedSection hasSampleTrackingWriteAccess>
                  <SampleTrackingDuplicateSamplesModal
                    samplesToCheck={selectedSampleIds.reduce<SelectedSampleInfo[]>((acc, i) => {
                      const item = rows.find(j => j.sampleId === i);

                      if (item) {
                        acc.push({
                          sampleId: item.sampleId,
                          sampleIdentifier: item.newId ?? item.inputId ?? '',
                          patientDeidentity: item.patientDeidentity,
                        } as SelectedSampleInfo);
                      }
                      return acc;
                    }, [])}
                    researchProjectId={researchProjectId}
                    autoConfirmWhenNoResultsFound={true}
                    onConfirm={(_, unselectedSampleIds, reloadData, ___) => {
                      // de-select the un-selected samples, leaving only the explicitly selected ones and any that were not duplicates
                      const unselectedSet = new Set(unselectedSampleIds);

                      const newSelectionModel = (selectionModel ?? [])
                        .map(selected => {
                          const id = apiRef.current?.getRow(selected)?.sampleId;
                          if (!id) {
                            return -1; // remove the sample from the selection
                          }

                          // if the sample is in the unselected set
                          if (unselectedSet.has(id)) {
                            return -1; // remove the sample from the selection
                          }

                          // Everything else stays selected
                          return selected;
                        })
                        .filter(selected => (selected as number) > -1);

                      // only need to reload data if there are unselected samples or if explicitly requested
                      refreshData(unselectedSet.size > 0 || reloadData, newSelectionModel as GridRowSelectionModel);
                    }}
                  />
                  {/* Removing this but not permanently. See: https://ovation-io.atlassian.net/browse/DATA-1993*/}
                  {/* <SampleTrackingExhaustModal
                    selectedSampleIds={selectedSampleIds}
                    onClose={refreshData}
                    completedSamplesSelected={completedSamplesSelected}
                  /> */}
                  <CreateSampleTrackingTransitionModal
                    researchProjectId={researchProjectId}
                    enableCheckForAlreadyProcessedSamples={true}
                    selectedSamples={selectedSampleIds.reduce<SelectedSampleInfo[]>((acc, i) => {
                      const item = rows.find(j => j.sampleId === i);

                      if (item) {
                        acc.push({
                          sampleId: item.sampleId,
                          sampleIdentifier: item.newId ?? item.inputId ?? '',
                          inputId: item.inputId,
                          selectionStatus: item.selectionStatus,
                          patientDeidentity: item.patientDeidentity,
                        } as SelectedSampleInfo);
                      }
                      return acc;
                    }, [])}
                    onClose={refreshData}
                    completedSamplesSelected={completedSamplesSelected}
                  />
                  {transitionType === 'Informatics' && configuredTransitionId && !!rows && (
                    <InformaticsCheckByModal
                      researchProjectId={researchProjectId}
                      data={getInformaticsCheckByTableData()}
                      onClose={refreshData}
                      configuredTransitionId={configuredTransitionId}
                    />
                  )}
                  {transitionType !== 'Informatics' && configuredTransitionId && (
                    <CheckByModal
                      researchProjectId={researchProjectId}
                      transitionType={transitionType}
                      configuredTransitionId={configuredTransitionId}
                      selectedSamples={selectedSampleIds.reduce<SelectedSampleInfo[]>((acc, i) => {
                        const item = rows.find(j => j.sampleId === i);

                        if (item) {
                          acc.push({
                            sampleId: item.sampleId,
                            sampleIdentifier: item.newId ?? item.inputId,
                            inputId: item.inputId,
                            inputLabAssignedId: item.inputLabAssignedId,
                            selectionStatus: item.selectionStatus,
                          } as SelectedSampleInfo);
                        }
                        return acc;
                      }, [])}
                      onClose={refreshData}
                      completedSamplesSelected={completedSamplesSelected}
                    />
                  )}
                  <SampleTrackingTagSamplesModal
                    researchProjectId={researchProjectId}
                    selectedSamples={map(selectedSampleIds, i => {
                      const item = rows.find(j => j.sampleId === i);

                      return { ...item, sampleTags: item?.sampleTags ?? [] } as any;
                    })}
                    onClose={(hasChanges: boolean) => {
                      if (hasChanges) {
                        setRefreshTrigger(currentState => !currentState);
                        setSelectionModel([]);
                      }
                    }}
                  />
                  <SampleTrackingRowSelectionModal
                    gridApiRef={apiRef}
                    sampleIdProperties={[foreignHash, sampleId, labAssignedSampleId, sampleBbid]}
                    setSelectionModel={setSelectionModel}
                    completedSamplesSelected={completedSamplesSelected}
                  />
                </AuthorizedSection>
              )}
              <AuthorizedSection hasSampleTrackingWriteAccess>{customActions ?? <></>}</AuthorizedSection>
            </FlexBox>
          }
          sampleHierarchyDialog={
            <>
              <DialogOpenButton onClick={() => setHistoryModalOpen(true)} sx={{ padding: 1, minWidth: 3 }}>
                <HistoryIcon />
              </DialogOpenButton>
              {historyModalOpen && (
                <SampleHierarchyDialog
                  sampleIds={selectedSampleIds.length > 0 ? selectedSampleIds : activeSampleIds}
                  completedSampleIds={completedSampleIds}
                  open={historyModalOpen}
                  onClose={() => setHistoryModalOpen(false)}
                  isReport={true}
                />
              )}
            </>
          }
          rows={rows}
          columns={columns}
          initialState={{
            pinnedColumns: {
              left: [GRID_CHECKBOX_SELECTION_FIELD, GRID_TREE_DATA_GROUPING_FIELD, sampleTag, copyBtn],
            },
            sorting: {
              sortModel: [
                { field: inputId, sort: 'asc' },
                { field: newId, sort: 'asc' },
                { field: sequenceRunId, sort: 'desc' },
                { field: checkByStatus, sort: 'asc' },
                { field: enteredAt, sort: 'asc' },
              ],
            },
          }}
          checkboxSelection={!disableActions}
          getTreeDataPath={row => row.groupByPath || []}
          treeData={groupByEnabled}
          groupingColDef={() => {
            return (
              selectedGroupByAttribute?.groupingColumnDef() || {
                headerName: t('unknown'),
                minWidth: 300,
              }
            );
          }}
          defaultGroupingExpansionDepth={1}
          disableRowSelectionOnClick
          onRowSelectionModelChange={newSelectionModel => setSelectionModel(newSelectionModel)}
          rowSelectionModel={selectionModel}
          getRowClassName={params => {
            if (completedSampleIds && completedSampleIds.includes(params.row.sampleId)) {
              return classes.completedRow;
            }
            return !params.row.exitedAt ? '' : classes.disabledRow;
          }}
          loadingProps={[loadingState, setLoadingState]}
          defaultColumnOrdering={[
            sampleTag,
            copyBtn,
            inputId,
            newId,
            sequenceType,
            sequenceRunId,
            informaticsRunStatus,
            r1FastQLocation,
            r2FastQLocation,
            checkByStatus,
            checkByFailedReason,
            qcStatus,
            stGridFulfillmentPlanSampleSelectionStatus,
            stGridSampleSampleTypeId,
            enteredAt,
            exitedAt,
            transitionId,
          ]}
          cacheKey={`sample-tracking-${configuredTransitionId}-sample-grid`}
          columnDefinitionOverrides={sampleTrackingGridDefaultColumnOverrides}
          componentName={COMPONENT_NAME}
          configuredTransitionId={configuredTransitionId}
          transitionEnumId={transitionEnum?.transitionEnumId}
          researchProjectId={researchProjectId}
        />
      </SampleTrackingConfigProvider>
      <WarningIndicator loadingState={loadingState} />
    </FlexTableBox>
  );
};

const useRows = (
  data: ReadonlyArray<SampleTrackingTableData>,
  sampleSequencingDetails: ReadonlyArray<SampleSequencingDetails>,
  filterInactiveTransitionSamples: boolean,
  useFilterOutOriginal: boolean,
  postDataFilter: ((rows: Row[]) => Row[]) | undefined,
  selectedGroupByAttribute?: TransitionGroupByAttribute | undefined
) => {
  return useMemo(() => {
    const sampleSequencingBySampleId = keyBy(sampleSequencingDetails, r => r.sampleId);

    const originalIdsToFilter: string[] = uniq(
      data
        .filter(d => {
          const isRemaining = d.transitionSample.checkByStatus === 'NotYetDecided' && !d.hasWorkLeft;
          return isRemaining;
        })
        .map(d => d.sample.sampleId)
    );

    let rows: Row[] = data
      .filter(r => !filterInactiveTransitionSamples || !r.transitionSample.exitedAt)
      .map((r, index) => {
        const originalSample = r.antecedentSample;

        const wasCreatedInThisTransition =
          originalSample &&
          r.antecedentTransitionSample &&
          r.transitionSample &&
          r.antecedentTransitionSample.transitionId === r.transitionSample.transitionId;

        const identifiers =
          wasCreatedInThisTransition && originalSample
            ? {
                // Has antecedent and was created in this transition - show as daughter
                filterId: '',
                inputSampleId: originalSample.sampleId,
                newSampleId: r.sample.sampleId,
                inputId: getTransitionSampleIdentifier(r.antecedentLabAssignedSampleLabel, originalSample),
                newId: getTransitionSampleIdentifier(r.labAssignedSampleLabel, r.sample),
                inputSampleBbid: originalSample.sampleBbid,
                inputForeignHash: originalSample.foreignHash,
                inputLabAssignedId: r.antecedentLabAssignedSampleLabel?.label ?? '',
                patientDeidentity: r.sample.patientDeidentity,
                newSampleBbid: r.sample.sampleBbid,
                newForeignHash: r.sample.foreignHash,
                newLabAssignedId: r.labAssignedSampleLabel?.label,
              }
            : {
                // Either no antecedent or antecedent from previous transition - show as input
                filterId: r.sample.sampleId,
                inputSampleId: r.sample.sampleId,
                inputId: getTransitionSampleIdentifier(r.labAssignedSampleLabel, r.sample),
                inputSampleBbid: r.sample.sampleBbid,
                inputForeignHash: r.sample.foreignHash,
                inputLabAssignedId: r.labAssignedSampleLabel?.label ?? '',
                patientDeidentity: r.sample.patientDeidentity,
              };

        if (selectedGroupByAttribute) {
          const originalRowForGroupBy = originalSample
            ? {
                sample: originalSample,
                labAssignedSampleLabel: r.antecedentLabAssignedSampleLabel,
                transitionSample: r.antecedentTransitionSample,
              }
            : undefined;
          selectedGroupByAttribute.groupByPath(identifiers as Row, r, originalRowForGroupBy as any);
        }

        // check if antecedent is in the same order
        return {
          id: index,
          ...sampleSequencingBySampleId[r.sample.sampleId ?? ''],
          ...identifiers,
          ...r.sample,
          ...r.transitionSample,
          labAssignedSampleId: r.labAssignedSampleLabel?.label,
        };
      })
      .filter(r => !useFilterOutOriginal || !originalIdsToFilter.includes(r.filterId));

    if (postDataFilter) {
      rows = postDataFilter(rows);
    }

    return rows;
  }, [
    data,
    filterInactiveTransitionSamples,
    useFilterOutOriginal,
    sampleSequencingDetails,
    postDataFilter,
    selectedGroupByAttribute,
  ]);
};

const useColumns = (
  transitionType: TransitionType,
  data: ReadonlyArray<SampleTrackingTableData>,
  checkByFailedReasons: ReadonlyArray<CheckByFailedReason>,
  columnsToHide: ReadonlyArray<string> | undefined
): GridColDef[] => {
  const { t } = useMemoTranslation();

  return useMemo(() => {
    const sequencingCols: GridColDef[] =
      transitionType === 'Sequencing' || transitionType === 'Informatics'
        ? [
            {
              field: r1FastQLocation,
              headerName: t(r1FastQLocation),
              headerAlign: 'left',
              align: 'left',
              minWidth: 200,
              flex: 2,
              valueGetter: params =>
                find(params.row.fastQs, i => (i as SequencingFastQ).type.name === 'r1')?.file.location,
            },
            {
              field: r2FastQLocation,
              headerName: t(r2FastQLocation),
              headerAlign: 'left',
              align: 'left',
              minWidth: 200,
              flex: 2,
              valueGetter: params =>
                find(params.row.fastQs, i => (i as SequencingFastQ).type.name === 'r2')?.file.location,
            },
          ]
        : [];

    const informaticsCols: GridColDef[] =
      transitionType === 'Informatics'
        ? [
            {
              field: sequenceType,
              headerName: t(sequenceType),
              headerClassName: compactGridHeaderClassName,
              width: 100,
              headerAlign: 'center',
              align: 'center',
              valueFormatter: ({ value }) => (value ? value.toUpperCase() : value),
            },
            {
              field: sequenceRunId,
              headerName: t(multiQc),
              headerClassName: compactGridHeaderClassName,
              headerAlign: 'center',
              align: 'center',
              width: 100,
              filterable: false,
              sortable: false,
              renderCell: renderCellMultiQcDownloadButton,
            },
            {
              field: informaticsRunStatus,
              headerName: t(informaticsRunStatus),
              headerClassName: compactGridHeaderClassName,
              headerAlign: 'center',
              align: 'center',
              width: 100,
              filterable: false,
              sortable: false,
              valueGetter: params => params.row.sequenceRunId,
              renderCell: renderGridCellInformaticsRunningIndicator,
            },
          ]
        : [];

    return (
      [
        sampleTagGridColumnDef(sampleTag, t(sampleTag), t),
        {
          field: copyBtn,
          headerName: t(copyBtn),
          headerAlign: 'center',
          align: 'center',
          maxWidth: 30,
          renderCell: renderCopyTextGridCell,
          valueGetter: params => params.row.newId ?? params.row.inputId,
        },
        {
          field: inputId,
          headerName: t(inputId),
          headerClassName: compactGridHeaderClassName,
          headerAlign: 'left',
          align: 'left',
          width: 250,
          minWidth: 250,
          renderCell: params =>
            renderSampleHierarchyGridCell({
              ...params,
              sampleId: params.row.inputSampleId,
              displayValue: params.value,
            }),
        },
        {
          field: newId,
          headerName: t(newId),
          headerClassName: compactGridHeaderClassName,
          headerAlign: 'left',
          align: 'left',
          width: 250,
          minWidth: 250,
          renderCell: params =>
            renderSampleHierarchyGridCell({
              ...params,
              sampleId: params.row.newSampleId,
              displayValue: params.value,
            }),
        },
        {
          field: inputSampleBbid,
          headerName: t(inputSampleBbid),
          headerClassName: compactGridHeaderClassName,
          headerAlign: 'left',
          align: 'left',
          width: 300,
          renderCell: params =>
            renderSampleHierarchyGridCell({
              ...params,
              sampleId: params.row.sampleId,
              displayValue: params.value,
            }),
        },
        {
          field: inputForeignHash,
          headerName: t(inputForeignHash),
          headerClassName: compactGridHeaderClassName,
          headerAlign: 'left',
          align: 'left',
          width: 300,
          renderCell: params =>
            renderSampleHierarchyGridCell({
              ...params,
              sampleId: params.row.sampleId,
              displayValue: params.value,
            }),
        },
        {
          field: inputLabAssignedId,
          headerName: t(inputLabAssignedId),
          headerClassName: compactGridHeaderClassName,
          headerAlign: 'left',
          align: 'left',
          width: 300,
        },
        {
          field: inputSampleId,
          headerName: t(inputSampleId),
          headerClassName: compactGridHeaderClassName,
          headerAlign: 'left',
          align: 'left',
          width: 300,
        },
        {
          field: newSampleBbid,
          headerName: t('daughterSampleBbid'),
          headerClassName: compactGridHeaderClassName,
          headerAlign: 'left',
          align: 'left',
          width: 300,
          renderCell: params =>
            renderSampleHierarchyGridCell({
              ...params,
              sampleId: params.row.sampleId,
              displayValue: params.value,
            }),
        },
        {
          field: newForeignHash,
          headerName: t('daughterForeignHash'),
          headerClassName: compactGridHeaderClassName,
          headerAlign: 'left',
          align: 'left',
          width: 300,
          renderCell: params =>
            renderSampleHierarchyGridCell({
              ...params,
              sampleId: params.row.sampleId,
              displayValue: params.value,
            }),
        },
        {
          field: newLabAssignedId,
          headerName: t('daughterLabAssignedId'),
          headerClassName: compactGridHeaderClassName,
          headerAlign: 'left',
          align: 'left',
          width: 300,
        },
        {
          field: newSampleId,
          headerName: t('daughterSampleId'),
          headerClassName: compactGridHeaderClassName,
          headerAlign: 'left',
          align: 'left',
          width: 300,
        },
        ...informaticsCols,
        ...sequencingCols,
        {
          field: checkByStatus,
          headerName: t(checkByStatus),
          headerClassName: compactGridHeaderClassName,
          headerAlign: 'center',
          align: 'center',
          width: 200,
          valueFormatter: ({ value }) => value && t(value),
        },
        {
          field: checkByFailedReason,
          headerName: t(checkByFailedReason),
          headerClassName: compactGridHeaderClassName,
          headerAlign: 'left',
          align: 'left',
          width: generateWidthFromProperty(
            data ?? [],
            200,
            (row: SampleTrackingTableData) => row.transitionSample.checkByFailedReason
          ),
          valueFormatter: ({ value }) => value && find(checkByFailedReasons, i => i.name === value)?.displayName,
        },
        {
          field: qcStatus,
          headerName: t(qcStatus),
          headerClassName: compactGridHeaderClassName,
          headerAlign: 'center',
          align: 'center',
          width: 100,
          valueFormatter: ({ value }) => value && t(value),
        },
        {
          field: enteredAt,
          headerName: t(enteredAt),
          headerClassName: compactGridHeaderClassName,
          headerAlign: 'center',
          align: 'center',
          minWidth: 200,
          type: 'date',
          valueGetter: dateGetter,
        },
        {
          field: exitedAt,
          headerName: t(exitedAt),
          headerClassName: compactGridHeaderClassName,
          headerAlign: 'center',
          align: 'center',
          minWidth: 200,
          type: 'date',
          valueGetter: dateGetter,
        },
        {
          field: createdAt,
          headerName: t(wallClock),
          headerAlign: 'left',
          align: 'left',
          minWidth: 100,
          type: 'date',
          valueGetter: dateGetter,
        },
        {
          field: transitionId,
          headerName: t(transitionId),
          headerClassName: compactGridHeaderClassName,
          headerAlign: 'center',
          align: 'center',
          width: 150,
          renderCell: renderGuidCellTooltip,
        },
      ] as GridColDef[]
    ).filter(i => !columnsToHide?.includes(i.field)) as GridColDef[];
  }, [t, transitionType, data, checkByFailedReasons, columnsToHide]);
};
